var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
export var safePatches = function (patches) {
    return patches.map(function (p) { return (__assign(__assign({}, p), { inputs: p.inputs || [], outputs: p.outputs || [] })); });
};
/** find a patch in the patchwork with an edge id */
export var findPatch = function (edgeId, patchwork) {
    var edge = patchwork.edges.find(function (p) { return p.id === edgeId; });
    if (edge) {
        return patchwork.patches.find(function (p) { return p.id === edge.to_patch; }) || null;
    }
    return null;
};
/** find a patch's index value in the patchwork with an edge id */
export var findPatchIndex = function (edgeId, patchwork) {
    var edge = patchwork.edges.find(function (p) { return p.id === edgeId; });
    if (edge) {
        return patchwork.patches.find(function (p) { return p.id === edge.to_patch; }) || -1;
    }
    return -1;
};
/**
 * ids of currently-selected patches that still exist in the patchwork.
 * A refetch or deletion can rebuild the node list without going through
 * ReactFlow's selection-change event, leaving stale ids behind that later
 * crash onPatchDelete (Sentry CMS-6TT). Returns the same array reference
 * when nothing changed, so callers can skip a state update.
 */
export var pruneSelectedPatchIds = function (selectedPatchIds, patches) {
    var patchIds = new Set(patches.map(function (p) { return p.id; }));
    var next = selectedPatchIds.filter(function (id) { return patchIds.has(id); });
    return next.length === selectedPatchIds.length ? selectedPatchIds : next;
};
/** deselect every node — used when an edge click should take over selection */
export var deselectAllNodes = function (nodes) {
    return nodes.map(function (n) { return (n.selected ? __assign(__assign({}, n), { selected: false }) : n); });
};
/** mark only `edgeId` as selected, leaving other edges untouched otherwise */
export var selectOnlyEdge = function (edges, edgeId) {
    return edges.map(function (e) {
        return e.selected === (e.id === edgeId) ? e : __assign(__assign({}, e), { selected: e.id === edgeId });
    });
};
/**
 * Pure decision logic behind onPatchDelete: resolves the raw canvas
 * selection (which can include stale ids, per Sentry CMS-6TT) into the
 * actual patch ids to delete, cascading a logic_group into its member
 * patches, and flags whether deleting drops the open group below 2 patches.
 */
export var resolvePatchesToDelete = function (entityIds, patchwork) {
    var _a = patchwork || {}, _b = _a.patches, patches = _b === void 0 ? [] : _b, _c = _a.logicGroups, logicGroups = _c === void 0 ? {} : _c, _d = _a.group, group = _d === void 0 ? null : _d;
    var indexedPatches = patches.reduce(function (acc, item) {
        acc[item.id] = item;
        return acc;
    }, {});
    var selectedPatches = [];
    var shouldExitGroup = false;
    entityIds.forEach(function (pId) {
        var patch = indexedPatches[pId];
        if (!patch)
            return;
        if (patch.type === 'logic_group') {
            var toDelete = (logicGroups[patch.patch_group] || {
                patches: [],
            }).patches;
            toDelete.forEach(function (p) { return selectedPatches.push(p.id); });
        }
        else {
            if (group !== null && patches.length === 2) {
                shouldExitGroup = true;
            }
            selectedPatches.push(patch.id);
        }
    });
    return { selectedPatches: selectedPatches, shouldExitGroup: shouldExitGroup };
};
/** shape of `selectedEntity` for a selected patch, or `false` to clear it */
export var buildPatchSelection = function (patch) {
    var _a;
    if (!patch)
        return false;
    return __assign({ type: 'patch', id: patch.id, deletable: patch.deletable, patchType: patch.type, is_custom_logic: !!(patch === null || patch === void 0 ? void 0 : patch.is_custom_logic), custom_logic_count: patch === null || patch === void 0 ? void 0 : patch.custom_logic_count }, ((patch === null || patch === void 0 ? void 0 : patch.type) ? (_a = {}, _a[patch.type] = patch === null || patch === void 0 ? void 0 : patch[patch.type], _a) : {}));
};
/**
 * Decides the state update for selecting an edge, or `null` for a no-op.
 * Note: passing a falsy `edge` also resolves to `null` here — this mirrors
 * pre-existing behavior where onEdgeSelected(null) never actually clears
 * the selection (the clearing branch is unreachable), which callers instead
 * work around by calling onDeselect() directly.
 */
export var resolveEdgeSelection = function (edge, currentSelectedEntity) {
    if (!edge)
        return null;
    if (currentSelectedEntity && edge.id === currentSelectedEntity.id) {
        return null;
    }
    return {
        selectedEntity: { type: 'edge', id: edge.id, deletable: true },
        propertiesBarView: 'edge',
    };
};
